/**
 * @author NikolaV
 */
import { action, computed, makeObservable, observable } from 'mobx';
import { Ajax } from '@shared/Ajax';
import { distanceBetween } from '@openlayers/ol-utils';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { store } from '@pages/trucks/store/rootStore';


export class TrucksStore implements ICrudStore<ITruck> {
    @observable items: ITruck[] = [];
    @observable sortKey = 'lastReport';

    @observable totalProjectVolume: number = 0;
    @observable totalProjectMassHaulTime: number = 0;
    @observable isDoingAsyncWork: boolean = false;
    @observable highlightIds = [];

    constructor() {
        makeObservable(this);
    }

    @computed
    get activeProjectVehicles(): ITruck[] {
        return this.items.filter(v => v.activeProject && v.activeProject.id === kuura.getProjectId()).sort(this.sortComparator);
    }

    @computed
    get movedVehicles(): ITruck[] {
        return this.items.filter(v => !v.activeProject || v.activeProject.id !== kuura.getProjectId()).sort(this.sortComparator);
    }

    // auto refresh
    private readonly updateIntervalMs = 30000;
    private readonly updateStatsIntervalMs = 120000;
    private updateInterval = null;
    private updateStatsInterval = null;

    // for debug
    private readonly DEBUG = false;
    private tick = 0;

    fetchWithStats() {
        this.isDoingAsyncWork = true;
        return this.fetch().done(() => {
            this.fetchStats();
            this.isDoingAsyncWork = false;
            this.startUpdatesInterval();
        });
    }

    fetch = () => {
        return Ajax({
            url: 'ajax_trucks.json',
        }).done(resp => {
            if (!resp.movedVehicles) {
                resp.movedVehicles = [];
            }
            const allVehicles: IVehicle[] = [...resp.vehicles, ...resp.movedVehicles];

            allVehicles.forEach((v: ITruck) => {
                if (!v) return;

                if (this.DEBUG) {
                    v.position.latitude += ((this.tick + Math.random()) / 1000);
                    v.position.longitude += ((this.tick + Math.random()) / 1000);
                    v.lastReport += (this.tick * this.updateIntervalMs);
                }

                // try to merge/update existing vehicles
                const existingVehicle = this.items.find(i => i.id == v.id);
                if (existingVehicle) {
                    this.updateExistingVehicleDetails(existingVehicle, v);
                }
                else {
                    v.positionHistory = [v.position];
                    v.speed = 0.0;
                    v.totalTaskTime = 0;
                    v.totalVolume = 0;
                }
            });

            // is initial load
            if (this.items.length === 0) {
                this.items = allVehicles;
            }
        });
    }

    fetchStats = () => {
        return Ajax({
            url: '/project_mass_haul_stats.json',
        }).done(resp => {
            let totalVolume = 0;
            let totalTime = 0;

            resp.vehicle_stats.forEach(vStats => {
                totalVolume += vStats.totalVolume;
                totalTime += vStats.totalTaskTime;

                const vehicleItem = this.items.find(i => i.id == vStats.vehicleId);
                if (vehicleItem) {
                    vehicleItem.totalTaskTime = vStats.totalTaskTime;
                    vehicleItem.totalVolume = vStats.totalVolume;
                }
                else {
                    console.warn('Can\'t find the vehicle to update stats');
                }
            });

            this.totalProjectMassHaulTime = totalTime;
            this.totalProjectVolume = totalVolume;
        });
    }

    startUpdatesInterval = () => {
        this.stopUpdatesInterval();
        this.updateInterval = setInterval(() => {
            this.fetch();
            this.tick++;
        }, this.updateIntervalMs);

        this.updateStatsInterval = setInterval(this.fetchStats, this.updateStatsIntervalMs);
    };

    stopUpdatesInterval = () => {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
        if (this.updateStatsInterval) {
            clearInterval(this.updateStatsInterval);
        }
    };

    @action
    updateExistingVehicleDetails(existingVehicle: ITruck, updatedVehicle: ITruck) {
        const distance = distanceBetween([existingVehicle.position.latitude, existingVehicle.position.longitude], [updatedVehicle.position.latitude, updatedVehicle.position.longitude]);
        const time = Math.floor((updatedVehicle.lastReport - existingVehicle.lastReport) / 1000); // time in hours
        const speed = (distance / time) * 3.6; // m/s to km/h
        if ($.isNumeric(speed) && isFinite(speed)) {
            existingVehicle.speed = parseFloat(speed.toFixed(1));
        }

        // update location
        if (existingVehicle.position.latitude != updatedVehicle.position.latitude) {
            existingVehicle.positionHistory.unshift(updatedVehicle.position);
        }
        existingVehicle.position.longitude = updatedVehicle.position.longitude;
        existingVehicle.position.latitude = updatedVehicle.position.latitude;
        existingVehicle.lastReport = updatedVehicle.lastReport;
        existingVehicle.driverName = updatedVehicle.driverName;
    }

    fetchVehicle(vehicleId: number): JQueryPromise<{
        vehicle: IVehicle,
        loadAreaId: number,
        unloadAreaId: number
    }> {
        return Ajax({
            url: '/trucks/vehicle.json',
            data: {
                vehicleId,
            },
        });
    }

    editVehicle(vehicleId: number, loadAreaId: number, unloadAreaId: number, driverName: string, driverPhone: string, vehicleInfo: string, volume: number) {
        const data = {
            vehicleId,
            loadAreaId,
            unloadAreaId,
            driverName,
            driverPhone,
            vehicleInfo,
            volume
        };

        if (!kuura.hasAdvancedTrucksEnabled()) {
            delete data.volume;
            delete data.loadAreaId;
            delete data.unloadAreaId;
        }

        return Ajax({
            url: '/trucks/vehicle/edit.json',
            method: 'post',
            data,
        }).done(() => {
            store.geofences.fetch();
            const existingVehicle = this.items.find(i => i.id == vehicleId);
            if (existingVehicle) {
                existingVehicle.driverName = driverName;
            }
        });
    }

    private sortComparator = (a: ITruck, b: ITruck) => {
        if (this.sortKey === 'lastReport') {
            [a, b] = [b, a];
            return alphaNumSort(a[this.sortKey] || 0, b[this.sortKey] || 0);
        }
        return alphaNumSort(a[this.sortKey], b[this.sortKey]);
    };
}
